import React, {Component} from 'react'
import propTypes from 'prop-types'
import {get, isEmpty, isNil} from 'lodash'
import Draggable from 'components/draggable'
import Droppable from 'components/droppable'
import {SortableElement} from 'react-sortable-hoc'
import Node from './node'
import iconNames from 'theme/icons'
import Placeholder from 'components/placeholder'
import './nodeTree.scss'
import _Node from './_node'

const _SortableNode = SortableElement(_Node)

class NodeTree extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dragging: false,
      dropping: false,
    }
  }

  getNodesForParent = (parent, nodes) => {
    return Array.isArray(nodes)
      ? nodes.filter(n => (get(n, 'parentKey', 0) || 0) === parent.key)
      : []
  }

  getIsActionEnabled = () => {
    const {node, isActionEnabled} = this.props
    return isNil(isActionEnabled) ? true : isActionEnabled(node)
  }

  dragOver = (e, data) => {
    const {node, onCanDrop} = this.props
    if (this.getIsActionEnabled()) {
      let doDrop = (data.type && data.type === 'treeView-node')
      if (!doDrop && onCanDrop) doDrop = onCanDrop(node, data)
      if (doDrop) this.setState({dropping: true})
    }
  }

  dragOut = () => {
    if (this.getIsActionEnabled()) {
      this.setState({dropping: false})
    }
  }

  drop = (e, data) => {
    if (this.getIsActionEnabled()) {
      if (this.state.dropping) {
        this.setState({dropping: false})
        const {node, onMove, onDrop} = this.props
        if (data.type && data.type === 'treeView-node') {
          if (onMove) onMove(data.node, node)
        } else {
          if (onDrop) onDrop(node, data)
        }
      }
    }
  }

  startDrag = () => {
    if (this.getIsActionEnabled()) {
      this.setState({dragging: true})
    }
  }

  endDrag = () => {
    if (this.getIsActionEnabled()) {
      this.setState({dragging: false})
    }
  }

  isRootNode = (node) => {
    return node.key === 0
  }

  isRootNodeEmpty = (node, nodes) => {
    return this.isRootNode(node) && isEmpty(nodes)
  }

  isExpanded = (node, expandedNodes) => {
    return node.key === 0 || expandedNodes.indexOf(node.key) >= 0
  }

  getNodesClass = (expanded, dragging) => {
    let nodesClass = 'treeView-nodes'
    if (expanded) nodesClass += ' treeView-nodes-expanded'
    if (dragging) nodesClass += ' treeView-nodes-dragging'
    return nodesClass
  }

  isAddingChildren = (maxDepth, depth) => {
    return maxDepth === null || (maxDepth > depth)
  }

  sortByNodeOrder = (nodes, sortable) => {
    const {orderNodes} = this.props
    return sortable || orderNodes
      ? nodes.sort((a, b) => {
        if (a.order > b.order) return 1
        if (a.order < b.order) return -1
        return 0
      })
      : nodes
  }

  mapNodesToNodeTree = (myNodes) => {
    const {
      selectedNode, nodes, expandedNodes, customActions, showCounts, onClick, onToggleExpand,
      onAdd, onRename, onDelete, onMove, onCanDrop, onDrop, addLabel, addFolderIcon, folderIcon, selectedFolderIcon,
      collapsedIcon, expandedIcon, maxDepth, depth, sortable, onSortStart, onSortMove, onSortEnd, axis,
      lockAxis, isActionEnabled, ...otherProps
    } = this.props
    return myNodes.map(node => {
      return (
        <NodeTree
          key={node.key}
          {...otherProps}
          node={node}
          selectedNode={selectedNode}
          nodes={nodes}
          expandedNodes={expandedNodes}
          showCounts={showCounts}
          customActions={customActions}
          onClick={onClick}
          onToggleExpand={onToggleExpand}
          onAdd={onAdd}
          onSortStart={onSortStart}
          onSortMove={onSortMove}
          onSortEnd={onSortEnd}
          onDelete={onDelete}
          onRename={onRename}
          onMove={onMove}
          onCanDrop={onCanDrop}
          onDrop={onDrop}
          addLabel={addLabel}
          addFolderIcon={addFolderIcon}
          folderIcon={folderIcon}
          selectedFolderIcon={selectedFolderIcon}
          collapsedIcon={collapsedIcon}
          expandedIcon={expandedIcon}
          depth={depth + 1}
          maxDepth={maxDepth}
          sortable={sortable}
          useDragHandle={true}
          axis={axis}
          lockAxis={lockAxis}
          isActionEnabled={isActionEnabled}/>
      )
    })
  }

  render() {
    const {
      node,
      nodes,
      onClick,
      isPlaceholderVisible,
      placeholderData,
      maxDepth,
      depth,
      expandedNodes,
      sortable,
      selectedNode,
      showCounts,
      onToggleExpand,
      addLabel,
      addFolderIcon,
      onAdd,
      onRename,
      onDelete,
      folderIcon,
      selectedNodeRef,
      selectedFolderIcon,
      collapsedIcon,
      expandedIcon,
      isActionEnabled,
      onDrop,
      onMove,
      lockAxis,
      axis,
      actions,
    } = this.props
    const {dragging, dropping} = this.state

    const expanded = this.isExpanded(node, expandedNodes)
    const nodesClass = this.getNodesClass(expanded, dragging)
    const addChildren = this.isAddingChildren(maxDepth, depth)
    let content = null
    let totalNodes = 0

    if (addChildren) {
      let myNodes = this.getNodesForParent(node, nodes)
      myNodes = this.sortByNodeOrder(myNodes, sortable)
      totalNodes = myNodes.length

      content = this.isRootNodeEmpty(node, myNodes) && isPlaceholderVisible
        ? <Placeholder {...placeholderData}/>
        : this.mapNodesToNodeTree(myNodes)
    }

    let nodeEl = (
      <Node
        node={node}
        selected={(node.key || 0) === (selectedNode || 0)}
        selectedNodeRef={selectedNodeRef}
        expanded={expanded}
        dropping={dropping}
        hasContent={addChildren === false
          ? false
          : content !== null && content.length > 0
        }
        showCount={showCounts}
        customActions={actions}
        onClick={onClick}
        onToggleExpand={addChildren === false ? () => {
        } : onToggleExpand}
        addLabel={addLabel}
        addFolderIcon={addFolderIcon}
        onAdd={addChildren ? onAdd : null}
        onRename={onRename}
        onDelete={onDelete}
        folderIcon={folderIcon}
        selectedFolderIcon={selectedFolderIcon}
        collapsedIcon={collapsedIcon}
        expandedIcon={expandedIcon}
        sortable={sortable}
        isActionEnabled={isActionEnabled}
      >
        {get(node, 'children', null)}
      </Node>
    )

    if (onDrop || onMove) {
      if (onMove && node.key !== 0 && sortable === false) {
        nodeEl = (
          <Draggable
            active={this.getIsActionEnabled()}
            minDistance={5}
            data={{type: 'treeView-node', node}}
            onStart={this.startDrag}
            onDrop={this.endDrag}
          >
            {nodeEl}
          </Draggable>
        )
      }
      nodeEl = (
        <Droppable
          onDrop={this.drop}
          onDragOver={this.dragOver}
          onDragOut={this.dragOut}
        >
          {nodeEl}
        </Droppable>
      )
    }

    if (sortable) {
      return (
        <_SortableNode
          node={node}
          nodeEl={nodeEl}
          totalNodes={totalNodes}
          disabled={sortable === false || (sortable && depth === 0)}
          index={node.order || 0}
          nodesClass={nodesClass}
          axis={axis}
          lockAxis={lockAxis}
        >
          {content}
        </_SortableNode>
      )
    }

    return (
      <_Node
        node={node}
        nodeEl={nodeEl}
        totalNodes={totalNodes}
        disabled={sortable === false || (sortable && depth === 0)}
        index={node.order || 0}
        nodesClass={nodesClass}
      >
        {content}
      </_Node>
    )
  }
}

NodeTree.propTypes = {
  node: propTypes.object.isRequired,
  selectedNode: propTypes.oneOfType([propTypes.string, propTypes.number]),
  nodes: propTypes.array.isRequired,
  expandedNodes: propTypes.arrayOf(propTypes.oneOfType([propTypes.string, propTypes.number])),
  customActions: propTypes.array,
  showCounts: propTypes.bool,
  onClick: propTypes.func.isRequired,
  onToggleExpand: propTypes.func.isRequired,
  onAdd: propTypes.func,
  onRename: propTypes.func,
  onDelete: propTypes.func,
  onMove: propTypes.func,
  onCanDrop: propTypes.func,
  onDrop: propTypes.func,
  addLabel: propTypes.string.isRequired,
  addFolderIcon: propTypes.string.isRequired,
  folderIcon: propTypes.string.isRequired,
  selectedFolderIcon: propTypes.string.isRequired,
  collapsedIcon: propTypes.string.isRequired,
  expandedIcon: propTypes.string.isRequired,
  depth: propTypes.number.isRequired,
  maxDepth: propTypes.number,
  sortable: propTypes.bool.isRequired,
  onSortStart: propTypes.func,
  onSortMove: propTypes.func,
  onSortEnd: propTypes.func,
  axis: propTypes.string,
  lockAxis: propTypes.string,
  isActionEnabled: propTypes.func,
  isPlaceholderVisible: propTypes.bool,
  placeholderData: propTypes.shape({
    messageTitle: propTypes.string,
    messageIcon: propTypes.string,
    iconSize: propTypes.number,
    actions: propTypes.array,
  }),
  orderNodes: propTypes.bool,
  actions: propTypes.array,
  selectedNodeRef: propTypes.object,
}

NodeTree.defaultProps = {
  maxDepth: null,
  sortable: false,
  isActionEnabled: null,
  addLabel: 'Add a Folder',
  addFolderIcon: iconNames.addFolder,
  isPlaceholderVisible: false,
  placeholderData: {
    messageTitle: 'Looks like there are no folders',
    messageIcon: iconNames.folder,
    iconSize: 36,
    actions: [],
  },
  orderNodes: false,
}


export default NodeTree
